"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Menu, X } from "lucide-react";
import { useIsClient } from "@/src/hooks/useIsClient";

export type NavPillItem = {
  href: string;
  label: string;
  children?: readonly NavPillItem[];
};

type NavPillMenuProps = {
  items: readonly NavPillItem[];
  ariaLabel: string;
  variant?: "hero" | "default";
  visibility?: "desktop" | "mobile" | "all";
  className?: string;
};

function getInitialIndex(pathname: string, items: readonly NavPillItem[]) {
  return items.findIndex((item) =>
    item.href === "/"
      ? pathname === "/"
      : pathname === item.href || pathname.startsWith(`${item.href}/`),
  );
}

export default function NavPillMenu({
  items,
  ariaLabel,
  variant = "default",
  visibility = "desktop",
  className = "",
}: NavPillMenuProps) {
  const pathname = usePathname();
  const navRef = useRef<HTMLElement | null>(null);
  const itemRefs = useRef<Array<HTMLAnchorElement | null>>([]);
  const closeSubmenuTimerRef = useRef<number | null>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [openMobileSubmenu, setOpenMobileSubmenu] = useState<string | null>(
    null,
  );
  const [indicatorStyle, setIndicatorStyle] = useState({
    width: 0,
    x: 0,
    opacity: 0,
  });
  const hasMounted = useIsClient();

  const activeIndex = getInitialIndex(pathname, items);
  const selectedIndex = hoveredIndex ?? (activeIndex >= 0 ? activeIndex : null);

  const clearCloseSubmenuTimer = () => {
    if (closeSubmenuTimerRef.current === null) return;

    window.clearTimeout(closeSubmenuTimerRef.current);
    closeSubmenuTimerRef.current = null;
  };

  const openDesktopSubmenu = (index: number) => {
    clearCloseSubmenuTimer();
    setHoveredIndex(index);
  };

  const closeDesktopSubmenuSoon = () => {
    clearCloseSubmenuTimer();
    closeSubmenuTimerRef.current = window.setTimeout(() => {
      setHoveredIndex(null);
      closeSubmenuTimerRef.current = null;
    }, 260);
  };

  const closeMobileMenu = () => {
    setIsMobileOpen(false);
    setOpenMobileSubmenu(null);
  };

  useEffect(() => clearCloseSubmenuTimer, []);

  useEffect(() => {
    if (!hasMounted || visibility === "mobile") {
      return;
    }

    const updateIndicator = () => {
      const navElement = navRef.current;
      const targetElement =
        selectedIndex === null ? null : itemRefs.current[selectedIndex];

      if (!navElement || !targetElement) {
        setIndicatorStyle((current) => ({ ...current, opacity: 0 }));
        return;
      }

      const navRect = navElement.getBoundingClientRect();
      const targetRect = targetElement.getBoundingClientRect();

      setIndicatorStyle({
        width: targetRect.width,
        x: targetRect.left - navRect.left,
        opacity: 1,
      });
    };

    updateIndicator();
    const resizeObserver = new ResizeObserver(updateIndicator);

    if (navRef.current) {
      resizeObserver.observe(navRef.current);
    }

    itemRefs.current.forEach((element) => {
      if (element) {
        resizeObserver.observe(element);
      }
    });

    window.addEventListener("resize", updateIndicator);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateIndicator);
    };
  }, [hasMounted, selectedIndex, visibility]);

  if (visibility === "mobile") {
    const mobileMenu = (
      // ✅ التعديل الأول: شيل transition و opacity من الـ div الخارجي
      <div
        className="fixed inset-0 z-[999]"
        style={{ pointerEvents: isMobileOpen ? "auto" : "none" }}
        aria-hidden={!isMobileOpen}
      >
        {/* ✅ نقل الـ transition هنا على الـ backdrop */}
        <button
          type="button"
          aria-label="إغلاق القائمة"
          className={[
            "absolute inset-0 bg-[rgba(22,18,16,0.42)] backdrop-blur-[10px] transition duration-300",
            isMobileOpen ? "opacity-100" : "opacity-0",
          ].join(" ")}
          onClick={closeMobileMenu}
        />

        <nav
          aria-label={ariaLabel}
          className={[
            "absolute inset-3 flex flex-col overflow-hidden rounded-[2.1rem] border border-[rgba(238,49,106,0.34)] bg-[linear-gradient(180deg,rgba(255,244,234,0.98),rgba(255,244,234,0.96))] px-5 py-5 text-right shadow-[0_34px_90px_rgba(36,21,26,0.28)] transition-all duration-500 ease-out sm:inset-5",
            isMobileOpen
              ? "translate-y-0 scale-100 opacity-100"
              : "translate-y-5 scale-[0.97] opacity-0",
          ].join(" ")}
          onClick={(event) => event.stopPropagation()}
        >
          <div className="mb-6 flex items-center justify-between gap-3">
            <span className="text-sm font-black tracking-[0.18em] text-[var(--color-ink-muted)]">
              القائمة
            </span>
            <button
              type="button"
              aria-label="إغلاق القائمة"
              onClick={closeMobileMenu}
              className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-white text-[var(--color-ink)] shadow-[0_12px_22px_rgba(36,21,26,0.1)]"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="grid flex-1 content-start gap-3 overflow-y-auto pb-3">
            {items.map((item, index) => {
              const isCurrent = activeIndex === index;
              const hasChildren = Boolean(item.children?.length);
              const submenuKey = `${item.href}-${item.label}`;
              const isSubmenuOpen = openMobileSubmenu === submenuKey;

              return (
                <div
                  key={`${item.href}-${item.label}`}
                  className={[
                    "transition-all duration-500 ease-out",
                    isMobileOpen
                      ? "translate-y-0 opacity-100"
                      : "translate-y-6 opacity-0",
                  ].join(" ")}
                  style={{
                    transitionDelay: isMobileOpen
                      ? `${120 + index * 90}ms`
                      : "0ms",
                  }}
                >
                  <div
                    className={[
                      "flex min-h-16 items-center justify-between gap-3 rounded-[1.45rem] px-5 py-4 text-lg font-black transition-all duration-300",
                      isCurrent
                        ? "bg-[linear-gradient(135deg,var(--color-accent-strong),var(--color-accent-deep))] text-white shadow-[0_18px_34px_rgba(238,49,106,0.22)]"
                        : "bg-white/76 text-[var(--color-ink)] shadow-[0_12px_24px_rgba(36,21,26,0.08)]",
                    ].join(" ")}
                  >
                    <Link
                      href={item.href}
                      onClick={closeMobileMenu}
                      className="min-w-0 flex-1"
                    >
                      {item.label}
                    </Link>
                    {hasChildren ? (
                      <button
                        type="button"
                        aria-label={
                          isSubmenuOpen
                            ? "إغلاق قائمة العروض"
                            : "فتح قائمة العروض"
                        }
                        aria-expanded={isSubmenuOpen}
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          setOpenMobileSubmenu((current) =>
                            current === submenuKey ? null : submenuKey,
                          );
                        }}
                        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/60 text-current transition hover:bg-white"
                      >
                        <ChevronDown
                          className={[
                            "h-4 w-4 transition-transform duration-300",
                            isSubmenuOpen ? "rotate-180" : "",
                          ].join(" ")}
                        />
                      </button>
                    ) : null}
                  </div>

                  {hasChildren ? (
                    <div
                      className={[
                        "grid overflow-hidden pe-4 transition-[grid-template-rows,opacity,margin] duration-300 ease-out",
                        isSubmenuOpen
                          ? "mt-2 grid-rows-[1fr] opacity-100"
                          : "mt-0 grid-rows-[0fr] opacity-0",
                      ].join(" ")}
                    >
                      <div className="grid min-h-0 gap-2 overflow-hidden">
                        {item.children?.map((child, childIndex) => (
                          <Link
                            key={child.href}
                            href={child.href}
                            onClick={closeMobileMenu}
                            className={[
                              "rounded-[1.15rem] border border-[rgba(238,49,106,0.16)] bg-white/54 px-4 py-3 text-base font-bold text-[var(--color-ink-soft)] transition-all duration-500 ease-out hover:bg-[var(--color-paper)] hover:text-[var(--color-ink)] focus-visible:bg-[var(--color-paper)] focus-visible:text-[var(--color-ink)]",
                              isSubmenuOpen
                                ? "translate-y-0 opacity-100"
                                : "translate-y-3 opacity-0",
                            ].join(" ")}
                            style={{
                              transitionDelay: isSubmenuOpen
                                ? `${childIndex * 75}ms`
                                : "0ms",
                            }}
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </nav>
      </div>
    );

    return (
      <div className={["lg:hidden", className].filter(Boolean).join(" ")}>
        <button
          type="button"
          aria-label={isMobileOpen ? "إغلاق القائمة" : "فتح القائمة"}
          aria-expanded={isMobileOpen}
          onClick={() => {
            setIsMobileOpen((current) => {
              if (current) {
                setOpenMobileSubmenu(null);
              }

              return !current;
            });
          }}
          className={[
            "inline-flex h-12 w-12 items-center justify-center rounded-full border transition-all duration-300",
            variant === "hero"
              ? "border-[rgba(238,49,106,0.42)] bg-[var(--color-surface)] text-[var(--color-ink)] shadow-[0_12px_25px_rgba(238,49,106,0.08)] backdrop-blur"
              : "border-[rgba(238,49,106,0.24)] bg-[rgba(255,244,234,0.9)] text-[var(--color-ink)] shadow-[0_14px_28px_rgba(36,21,26,0.1)] backdrop-blur",
          ].join(" ")}
        >
          {isMobileOpen ? (
            <X className="h-5 w-5" />
          ) : (
            <Menu className="h-5 w-5" />
          )}
        </button>

        {hasMounted ? createPortal(mobileMenu, document.body) : null}
      </div>
    );
  }

  const visibilityClass = visibility === "all" ? "flex" : "hidden lg:flex";

  const navClasses = [
    "relative items-center isolate",
    variant === "hero"
      ? "border border-[rgba(255,244,234,0.12)] bg-[rgba(32,24,20,0.16)] backdrop-blur-[14px]"
      : "gap-6 bg-transparent p-0",
    "overflow-visible",
    visibilityClass,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <nav
      ref={navRef}
      aria-label={ariaLabel}
      className={navClasses}
      onMouseEnter={clearCloseSubmenuTimer}
    >
      {variant === "hero" ? (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute bottom-1.5 top-1.5 z-[1] rounded-full bg-[#ffa9003b] transition-all duration-300 ease-out"
          style={{
            width: `${indicatorStyle.width}px`,
            left: `${indicatorStyle.x}px`,
            opacity: indicatorStyle.opacity,
          }}
        />
      ) : null}

      {items.map((item, index) => {
        const isCurrent = hasMounted && selectedIndex === index;
        const isSubmenuOpen = hoveredIndex === index;

        return (
          <div
            key={`${item.href}-${item.label}`}
            className="group relative z-[2]"
            onMouseEnter={() => openDesktopSubmenu(index)}
            onMouseLeave={closeDesktopSubmenuSoon}
            onFocus={() => openDesktopSubmenu(index)}
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget)) {
                setHoveredIndex(null);
              }
            }}
          >
            <Link
              href={item.href}
              ref={(element) => {
                itemRefs.current[index] = element;
              }}
              className={[
                "relative z-[2] inline-flex min-h-11 shrink-0 items-center justify-center gap-1.5 rounded-[0.8rem] px-3 py-3 text-[1rem] font-semibold leading-none transition-colors duration-300",
                variant === "hero"
                  ? isCurrent
                    ? "text-[var(--color-hero-text)]"
                    : "text-[var(--color-hero-text-muted)] hover:text-[var(--color-hero-text)] focus-visible:text-[var(--color-hero-text)]"
                  : isCurrent
                    ? "text-[var(--color-accent)]"
                    : "text-[#242424] hover:bg-[rgba(238,55,106,0.08)] hover:text-[var(--color-accent)] focus-visible:bg-[rgba(238,55,106,0.08)] focus-visible:text-[var(--color-accent)]",
              ].join(" ")}
            >
              <span>{item.label}</span>
              {item.children?.length ? (
                <ChevronDown className="h-3.5 w-3.5" />
              ) : null}
              {variant !== "hero" && (isCurrent || hoveredIndex === index) ? (
                <span
                  aria-hidden="true"
                  className="absolute bottom-0 left-1/2 h-[3px] w-[78%] -translate-x-1/2 rounded-full bg-[var(--color-accent)]"
                />
              ) : null}
            </Link>

            {item.children?.length ? (
              <>
                <span
                  aria-hidden="true"
                  className={[
                    "absolute right-0 top-full w-full",
                    variant === "hero" ? "h-10" : "h-4",
                  ].join(" ")}
                  onMouseEnter={() => openDesktopSubmenu(index)}
                />
                <div
                  onMouseEnter={() => openDesktopSubmenu(index)}
                  onMouseLeave={closeDesktopSubmenuSoon}
                  className={[
                    "absolute right-0 z-[60] min-w-48 rounded-[1rem] border p-2 transition-all duration-300 group-hover:pointer-events-auto group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:translate-y-0 group-focus-within:opacity-100",
                    isSubmenuOpen
                      ? "pointer-events-auto translate-y-0 opacity-100"
                      : "pointer-events-none translate-y-1 opacity-0",
                    variant === "hero"
                      ? "top-[calc(100%+0.18rem)] border-white/14 bg-[rgba(32,24,20,0.78)]"
                      : "top-[calc(100%+0.45rem)] border-[rgba(241,89,34,0.14)] bg-white",
                  ].join(" ")}
                >
                  {item.children.map((child, childIndex) => (
                    <Link
                      key={child.href}
                      href={child.href}
                      className={[
                        "block rounded-[1rem] px-4 py-3 text-right text-sm font-bold transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:translate-y-0 group-focus-within:opacity-100",
                        isSubmenuOpen
                          ? "translate-y-0 opacity-100"
                          : "translate-y-2 opacity-0",
                        variant === "hero"
                          ? "text-white/78 hover:bg-[var(--color-paper)] hover:text-[var(--color-ink)] focus-visible:bg-[var(--color-paper)] focus-visible:text-[var(--color-ink)]"
                          : "text-[var(--color-ink)] hover:bg-[var(--color-paper)] focus-visible:bg-[var(--color-paper)]",
                      ].join(" ")}
                      style={{ transitionDelay: `${childIndex * 70}ms` }}
                    >
                      {child.label}
                    </Link>
                  ))}
                </div>
              </>
            ) : null}
          </div>
        );
      })}
    </nav>
  );
}
